const SITE_URL = 'https://symphony-strategies.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo-transparent.png`;

const ROUTE_SEO = {
  '/': {
    title: 'Symphony Strategies | Family Office Design & Coordination',
    description:
      'Symphony Strategies designs and operates family offices that integrate tax strategy, estate planning, and operating companies into coordinated systems.',
  },
  '/about': {
    title: 'About Symphony Strategies | Integrated Family Office Coordination',
    description:
      'Learn how Symphony Strategies coordinates CPAs, attorneys, insurance advisors, investment managers, and operating teams for entrepreneurial families.',
  },
  '/services': {
    title: 'Family Office Services | Tax, Estate, Insurance & Advisor Coordination',
    description:
      'Pre-transaction planning, estate and trust coordination, family office design, insurance strategy, reporting, governance, and implementation support.',
  },
  '/process': {
    title: 'Symphony Process | From Planning Gaps to Coordinated Execution',
    description:
      'A structured process for mapping advisor ecosystems, finding planning gaps, coordinating implementation, and keeping family-office work moving.',
  },
  '/outcomes': {
    title: 'Family Office Outcomes | Planning ROI, Governance & Execution',
    description:
      'See the types of outcomes Symphony Strategies helps families pursue: planning ROI, faster execution, cleaner governance, and coordinated advisors.',
  },
  '/contact': {
    title: 'Contact Symphony Strategies | Start a Family Office Conversation',
    description:
      'Start a conversation with Symphony Strategies about family-office design, tax and estate planning coordination, and implementation support.',
  },
};

const normalizePath = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/$/, '');
};

const setOrCreateMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const setCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', href);
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Symphony Strategies',
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  description:
    'Symphony Strategies designs and operates family offices that integrate tax strategy, estate planning, and operating companies into coordinated systems.',
  sameAs: ['https://portal.symphony-strategies.com'],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Symphony Strategies',
  url: SITE_URL,
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'Symphony Strategies',
  url: SITE_URL,
  image: DEFAULT_IMAGE,
  serviceType: [
    'Family office design',
    'Estate planning coordination',
    'Tax strategy coordination',
    'Insurance and risk strategy coordination',
    'Pre-transaction planning',
  ],
  areaServed: 'United States',
  provider: {
    '@type': 'Organization',
    name: 'Symphony Strategies',
    url: SITE_URL,
  },
};

const setSchema = () => {
  const id = 'symphony-structured-data';
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify([organizationSchema, websiteSchema, serviceSchema]);
};

export const getSeoForPath = (pathname) => {
  const path = normalizePath(pathname);
  return ROUTE_SEO[path] || ROUTE_SEO['/'];
};

export const applySeo = (pathname) => {
  if (typeof document === 'undefined') return;
  const path = normalizePath(pathname);
  const seo = getSeoForPath(path);
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;

  document.title = seo.title;
  setCanonical(url);
  setOrCreateMeta('meta[name="description"]', { name: 'description', content: seo.description });
  setOrCreateMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
  setOrCreateMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Symphony Strategies' });
  setOrCreateMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
  setOrCreateMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
  setOrCreateMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  setOrCreateMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
  setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
  setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
  setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });
  setSchema();
};

export { SITE_URL, ROUTE_SEO };
